import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'
import Context from '../Context'
import theme from '../theme'
import EventTable from '../components/EventTable'
import useInterval from '../utils/useInterval'
import { apiJson, apiFetch } from '../utils/api'
import { getSessionId } from '../utils/session'

const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

/**
 * One opportunity, on its own.
 *
 * Deliberately the same table the league screen uses rather than a new way of showing prices: a trader
 * arriving from an alert should be looking at something they already know how to read, with the two
 * prices of the arb picked out. The event row carries its own Show all lines through to every rung.
 */
function ArbPage({ arbId, pollingInterval }) {
  const { polledHost, usPrices, username, setRequestCount, setLastResponseTime } = useContext(Context)

  const [ arb, setArb ] = useState(null)
  const [ event, setEvent ] = useState(null)
  const [ error, setError ] = useState('')

  useEffect(() => {
    let cancelled = false
    setArb(null)
    setEvent(null)
    setError('')

    apiJson(polledHost, `/api/notifications/one?id=${arbId}`)
      .then((data) => {
        if (!cancelled) {
          setArb(data.notification)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || 'Could not load this arb')
        }
      })

    return () => { cancelled = true }
  }, [polledHost, arbId])

  /**
   * The prices come from the same feed the grid reads, asked for one sport and narrowed to one fixture
   * here. Going to the feed rather than replaying what the notification stored means the page shows what
   * is on offer now, including the arb having since gone.
   */
  const loadEvent = useCallback(async () => {
    if (!arb) {
      return
    }
    try {
      const sessionId = getSessionId()
      const url = `/odds-comparison01?sessionId=${sessionId}&addAll=false&favOnly=false&minYield=-1&sport=${encodeURIComponent(arb.sport)}&bookmaker=Bovada`
      const response = await apiFetch(polledHost, url)
      const data = await response.json()

      let found = null
      for (const sport of data) {
        for (const league of sport.leagues || []) {
          for (const item of league.eventList || []) {
            if (item.home === arb.home && item.away === arb.away && Number(item.koTime) === Number(arb.koTime)) {
              found = item
            }
          }
        }
      }

      setEvent(found)
      setRequestCount((prev) => prev + 1)
      setLastResponseTime(Date.now())
      if (!found) {
        setError('This game is no longer in the feed.')
      } else {
        setError('')
      }
    } catch (e) {
      setError(e.message || 'Could not load the prices')
    }
    // eslint-disable-next-line
  }, [arb, polledHost, username])

  useEffect(() => { loadEvent() }, [loadEvent])
  useInterval(() => { loadEvent() }, pollingInterval)

  if (!arb && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><CircularProgress /></Box>
    )
  }

  return (
    <Box sx={{ padding: '16px 24px' }}>
      {arb && (
        <Box sx={{ marginBottom: '14px' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#6E7378' }}>
            <Box component="span" sx={{ fontSize: '19px', fontWeight: 700, color: theme.palette.primary.light, marginRight: '10px' }}>
              {arb.profit.toFixed(2)}%
            </Box>
            {arb.sport} · {arb.league} · {arb.market}
            {arb.legs && arb.legs.map((leg, index) => (
              <Box component="span" key={index} sx={{ marginLeft: '14px', whiteSpace: 'nowrap' }}>
                {leg.outcome} <Box component="span" sx={{ fontWeight: 700 }}>{leg.price}</Box>
                <Box component="span" sx={{ color: '#9A9EA3' }}> {leg.bookmaker}</Box>
              </Box>
            ))}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3' }}>
            Prices below are live. What the alert saw is above.
          </Typography>
        </Box>
      )}

      {error && (
        <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: theme.palette.secondary.main, marginBottom: '10px' }}>
          {error}
        </Typography>
      )}

      {/* the same wrapper the league screen uses: the data grid measures its parent on mount and comes
          up empty inside a plain box */}
      {event && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={9}>
            <EventTable
              key={`${event.home}|${event.away}|${event.koTime}`}
              event={event}
              usPrices={usPrices}
              showDiscrepancies={false}
              showArbs
            />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export default ArbPage
